"use client";
import React from 'react';
import Script from 'next/script';
import Link from 'next/link';
import styles from './Breadcrumbs.module.scss';

export interface BreadcrumbItem {
  text: string;
  href: string;
  isCurrent?: boolean;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items }) => {
  // Always prepend Home
  const breadcrumbs = [
    { text: 'Home', href: 'https://www.solflare.com', isCurrent: false },
    ...items,
  ];

  // Helper function to convert relative URLs to absolute for schema
  const getAbsoluteUrl = (href: string): string => {
    if (href.startsWith('http')) {
      return href; // Already absolute
    }
    // Convert relative URLs to absolute
    return `https://www.solflare.com${href.startsWith('/') ? href : `/${href}`}`;
  };

  // Schema markup with absolute URLs
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'name': 'Breadcrumbs',
    'itemListElement': breadcrumbs.map((item, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: item.text,
      item: getAbsoluteUrl(item.href), // Always use absolute URLs for schema
    })),
  };

  return (
    <>
      <Script
        id="breadcrumb-schema"
        type="application/ld+json"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <nav aria-label="Breadcrumb" className={styles.breadcrumbs}>
        {breadcrumbs.map((item, idx) => (
          <React.Fragment key={item.text + idx}>
            {item.isCurrent ? (
              <span className={styles['breadcrumb-current']}>{item.text}</span>
            ) : (
              <Link
                href={item.href}
                className={styles['breadcrumb-link']}
                aria-current={item.isCurrent ? 'page' : undefined}
              >
                {item.text}
              </Link>
            )}
            {idx < breadcrumbs.length - 1 && (
              <span className={styles['breadcrumb-separator']}>/</span>
            )}
          </React.Fragment>
        ))}
      </nav>
    </>
  );
};
